import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarDays, Clock, MapPin, Users, Trophy, Info } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import LeagueRegistration from "./LeagueRegistration";
// UPPDRAG-38: tier-aware pricing — replaces the binary "Medlemmar spelar gratis"
// badge with a per-player calculation that considers the player's actual tier.
import { useAuth } from "@/hooks/useAuth";
import { loadLeaguePricingForPlayer } from "@/lib/leaguePricing";
// UPPDRAG-40: rich-text rendering for league rules. RichTextDisplay sanitizes
// the HTML via DOMPurify and short-circuits to null for empty inputs, so the
// caller (LeagueInfoCard) still uses isRichTextEmpty to decide whether to
// render the rules card.
import { RichTextDisplay } from "@/components/ui/rich-text-display";
import { isRichTextEmpty } from "@/lib/sanitizeHtml";
// UPPDRAG-42: render multi-category info on the league info card.
import {
  resolveLeagueCategories,
  LEAGUE_CATEGORY_LABELS,
  formatLeagueCategoriesLabel,
} from "@/lib/leagueCategories";
import { Badge } from "@/components/ui/badge";

const dayLabels: Record<string, string> = {
  monday: "Måndag", tuesday: "Tisdag", wednesday: "Onsdag",
  thursday: "Torsdag", friday: "Fredag", saturday: "Lördag", sunday: "Söndag",
};

interface LeagueInfoCardProps {
  league: any;
  teamCount: number | null | undefined;
  myTeam: any;
  isOpen: boolean;
  alreadyRegistered: boolean;
  showRegistration: boolean;
  setShowRegistration: (v: boolean) => void;
}

export default function LeagueInfoCard({
  league, teamCount, myTeam, isOpen, alreadyRegistered, showRegistration, setShowRegistration,
}: LeagueInfoCardProps) {
  const matchFormat = league.match_format as any;
  // UPPDRAG-38: tier-aware price for the signed-in player. When user is null
  // (anonymous) → fullPrice. When user is signed in but has no active
  // membership in this org → fullPrice. With matching tier rule → discounted
  // or free per league_membership_pricing row.
  const { user } = useAuth();
  const { data: tierPricing } = useQuery({
    queryKey: ["league-tier-pricing", league.id, user?.id],
    queryFn: () => loadLeaguePricingForPlayer({
      leagueId: league.id,
      userId: user?.id ?? null,
      organizationId: league.organization_id,
      basePriceKr: Number(league.cost_per_player ?? 0),
    }),
    enabled: !!league?.id && !!league?.organization_id,
  });

  return (
    <div className="space-y-4">
      {/* Key info */}
      <Card>
        <CardContent className="p-4 space-y-3">
          {/* UPPDRAG-42: visa öppna kategorier som chips. När fler än en
              är vald visas alla; en enda visas som "{X}-serie". Tom → ingen
              rad alls (legacy-fall där fritexten inte mappar mot någon
              känd nyckel). */}
          {(() => {
            const cats = resolveLeagueCategories(league as { categories?: string[] | null; category?: string | null });
            if (cats.length === 0) return null;
            return (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  Öppen för:
                </span>
                {cats.map((c) => (
                  <Badge key={c} variant="outline" className="text-[10px]">
                    {LEAGUE_CATEGORY_LABELS[c]}
                  </Badge>
                ))}
                <span className="sr-only">
                  {formatLeagueCategoriesLabel(cats)}
                </span>
              </div>
            );
          })()}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span>{league.halls?.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>{league.match_duration_minutes} min/match</span>
            </div>
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
              <span>
                {league.play_days?.map((d: string) => dayLabels[d]?.slice(0, 3) || d).join(", ")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span>{teamCount ?? 0} lag anmälda</span>
            </div>
          </div>

          {league.play_time_start && league.play_time_end && (
            <p className="text-sm text-muted-foreground">
              Speltider: {league.play_time_start.slice(0, 5)}–{league.play_time_end.slice(0, 5)}
            </p>
          )}

          {league.start_date && league.end_date && (
            <p className="text-sm text-muted-foreground">
              Period: {format(new Date(league.start_date), "d MMM", { locale: sv })} – {format(new Date(league.end_date), "d MMM yyyy", { locale: sv })}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Format & scoring */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Matchformat</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0 text-sm space-y-1 text-muted-foreground">
          <p>{matchFormat?.sets} set, {matchFormat?.games_per_set} game per set</p>
          {matchFormat?.golden_point && <p>Golden point vid 40-40</p>}
          {matchFormat?.play_all_sets && <p>Alla set spelas</p>}
          {matchFormat?.time_limit_applies && <p>Tidsbegränsad match</p>}
          <p>Tiebreak vid {matchFormat?.tiebreak_at}</p>
        </CardContent>
      </Card>

      {/* Price */}
      {/* UPPDRAG-38: tier-aware price line. The base price always shows; if the
          player has a matching tier with a discount or is_free, a second line
          shows the discounted price + tier name. Falls back to legacy display
          when the tier query is still loading or returns full price. */}
      <Card>
        <CardContent className="p-4 flex items-center justify-between">
          <div>
            <p className="font-semibold">
              {league.cost_per_player > 0 ? `${league.cost_per_player} kr / spelare` : "Gratis"}
            </p>
            {tierPricing?.appliedDiscountPercent != null && tierPricing.appliedDiscountPercent > 0 && (
              <p className="text-sm text-primary">
                {tierPricing.isFree
                  ? "Som medlem: Gratis"
                  : `Som medlem: ${tierPricing.finalPriceKr} kr (${tierPricing.appliedDiscountPercent}% rabatt)`}
              </p>
            )}
            {/* Backward-compat: still show the legacy "Medlemmar spelar gratis"
                badge for leagues that have members_play_free=true but where the
                signed-in player has no membership match (so tierPricing is full
                price). Once members_play_free is dropped this becomes dead code. */}
            {league.members_play_free && tierPricing?.appliedDiscountPercent == null && (
              <p className="text-sm text-primary">Medlemmar spelar gratis</p>
            )}
          </div>
          {league.registration_deadline && (
            <div className="text-right text-sm text-muted-foreground">
              <p>Sista anmälningsdag</p>
              <p className="font-medium text-foreground">
                {format(new Date(league.registration_deadline), "d MMM yyyy", { locale: sv })}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rules */}
      {/* UPPDRAG-40: rules are now rich text (HTML). RichTextDisplay
          sanitizes via DOMPurify; isRichTextEmpty handles "<p></p>" /
          legacy plain-text "" / null uniformly. Legacy plain-text values
          render fine as-is — DOMPurify wraps loose text into the
          allow-listed structure. */}
      {!isRichTextEmpty(league.rules_text) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Info className="h-4 w-4" /> Regler
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <RichTextDisplay
              html={league.rules_text}
              className="text-sm text-muted-foreground"
            />
          </CardContent>
        </Card>
      )}

      {/* Registration CTA */}
      {isOpen && !alreadyRegistered && !showRegistration && (
        <Button className="w-full" size="lg" onClick={() => setShowRegistration(true)}>
          <Trophy className="h-5 w-5 mr-2" /> Anmäl lag
        </Button>
      )}

      {alreadyRegistered && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-4 text-center">
            <p className="font-medium text-primary">✓ Du är anmäld till denna serie</p>
            <p className="text-sm text-muted-foreground mt-1">
              Lagnamn: {myTeam?.team_name || "Inget lagnamn"}
            </p>
            {/* TASK-67 PR-D: the legacy myTeam.player2_invite_email +
                player2_invite_name placeholder columns are dropped by this
                PR's migration. The "Inväntar svar" indicator now reads from
                team_invitations (status='pending') — see the pendingInvite
                query below. The myTeam pre-fetch passes the team to its
                parent without invite info, so we look up the row by team_id
                directly. */}
            {myTeam?.id && <PendingInviteIndicator teamId={myTeam.id} />}
          </CardContent>
        </Card>
      )}

      {showRegistration && (
        <LeagueRegistration
          leagueId={league.id}
          onClose={() => setShowRegistration(false)}
        />
      )}
    </div>
  );
}

// TASK-67 PR-D: replaces the legacy `!myTeam.player2_id &&
// (myTeam.player2_invite_email || myTeam.player2_invite_name)` check which
// read columns dropped by this PR's migration. The new flow looks up
// pending team_invitations rows for the team and renders the invitee's
// email or formatted name as the "Inväntar svar från X" placeholder.
function PendingInviteIndicator({ teamId }: { teamId: string }) {
  const { data: pendingInvite } = useQuery({
    queryKey: ["team-pending-invite", teamId],
    queryFn: async () => {
      const { data } = await supabase
        .from("team_invitations")
        .select("invited_email")
        .eq("team_id", teamId)
        .eq("status", "pending")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return (data as { invited_email: string | null } | null) ?? null;
    },
    enabled: !!teamId,
  });

  if (!pendingInvite?.invited_email) return null;
  return (
    <p className="text-sm text-amber-600 mt-2">
      ⏳ Inväntar svar från {pendingInvite.invited_email}
    </p>
  );
}
